import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';
import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import useDealSplitPrompt, { dealSplitQuery } from '@components/modals/useDealSplitPrompt';

import { getGroupPoData } from '@features/order/utils/projectUtil';
import ColorSizeBreakDownTable from '@features/order/components/orderInfo/ColorSizeBreakDownTable';
import {
	useCreateProgramTypeMutation,
	useLazyGetAllProgramTypesQuery
} from '@features/order/api/OrderDetailsApi';
import {
	useAddAiPoExtractionMutation,
	useGetCollectionsQuery,
	useGetFolderCollectionsQuery
} from '@features/order/api/PoAutomationApi';
import { validateOrderStyles } from '@features/order/utils/automationIndex';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { getOrderDetailsInfo } from '@store/reducers';

import { fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { isAdmin, renderSetImages, STATIC_SET_CONSTANTS, validateFloatNumber } from '@utils/Util';

import { useGetProjectDealListQuery } from '@/features/sampleDevelopment/api/sampleDevelopmentApi';

const AddStyleFromCurrentOrder = ({
	orderId,
	isDrafted,
	hideStatusType,
	poItemIdentifier,
	productRefNumber,
	newlyAddedStyle,
	onRemoveStyles,
	onRemovePoExtractionStyle,
	onAddSuccess,
	getProjectDetails,
	pOSegmentationType
}) => {
	const dispatch = useDispatch();
	const { askDealSplit, dealSplitModal } = useDealSplitPrompt();
	const orderDetailsInfo = useSelector(getOrderDetailsInfo);
	const [orderStyles, setOrderStyles] = useState([]);
	const [isOverflow, setIsOverflow] = useState(false);

	const [groupData, setGroupData] = useState(null);

	const [, setSeasons] = useState([]);
	const [, setSetPackStyleFlag] = useState([]);
	const [designIdList, setDesignIdList] = useState([]);
	const [, setRemovedColorIds] = useState([]);
	const [, setProductWiseColorIds] = useState({});

	const [errorDesign, setErrorDesign] = useState({});
	const [, setIsPlacingOrder] = useState(false);

	const [, setOpenAccordion] = useState([]);

	const [programTypeOptionList, setProgramTypeOptionList] = useState([]);
	const [programTypeErrors, setProgramTypeErrors] = useState({});
	const [salesMarginErrors, setSalesMarginErrors] = useState({});
	const [dealErrors, setDealErrors] = useState({});

	const [fetchProgramTypeTrigger] = useLazyGetAllProgramTypesQuery();
	const [createProgramType] = useCreateProgramTypeMutation();
	const [addAiPoExtraction] = useAddAiPoExtractionMutation();

	const { data: projectDealList } = useGetProjectDealListQuery(
		{
			params: {
				brandId: orderDetailsInfo?.brandResponse?.id
			}
		},
		{
			skip: !orderDetailsInfo?.brandResponse?.id
		}
	);

	const hasPoExtractionStyle = newlyAddedStyle?.some((style) => style?.isFromPoExtraction);
	const { data: collectionData } = useGetCollectionsQuery(
		{ collectionId: orderDetailsInfo?.collectionId },
		{ skip: !hasPoExtractionStyle || !orderDetailsInfo?.collectionId, refetchOnMountOrArgChange: true }
	);
	const { data: folderCollectionData } = useGetFolderCollectionsQuery(
		{ folderId: orderDetailsInfo?.folderId },
		{ skip: !hasPoExtractionStyle || !orderDetailsInfo?.folderId, refetchOnMountOrArgChange: true }
	);

	const programTypeOptions = useMemo(() => {
		if (!Array.isArray(programTypeOptionList)) {
			return [];
		}
		return programTypeOptionList
			.map((item) => ({
				id: item?.id,
				name: item?.name
			}))
			.filter((item) => item.id && item.name);
	}, [programTypeOptionList]);

	useEffect(() => {
		setOrderStyles(newlyAddedStyle);
	}, [newlyAddedStyle]);

	useEffect(() => {
		const allProductRefNumbers = groupData?.map((item) => item.productReferenceNumber);
		setOpenAccordion(allProductRefNumbers);
	}, [groupData]);

	const handleAccordionClick = (productRefNumber) => {
		setOpenAccordion((prevState) => {
			const newState = prevState.includes(productRefNumber)
				? prevState.filter((ref) => ref !== productRefNumber)
				: [...prevState, productRefNumber];
			return newState;
		});
	};

	useEffect(() => {
		if (productRefNumber) {
			const element = document.getElementById(productRefNumber);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [productRefNumber]);
	useEffect(() => {
		if (orderStyles) {
			let ids = orderStyles.map((item) => item.productId);
			setDesignIdList(ids);
			if (!isDrafted) {
				let quotationColorIds = [];
				let productColorIds = [];
				orderStyles
					.map((styleItem) => styleItem.colorWiseSizeQuantityPairList)
					.forEach((colorItem) => {
						if (colorItem) {
							quotationColorIds.push(...colorItem.map((singleItem) => singleItem.id));
						}
					});
				orderStyles
					.map((styleItem) => styleItem?.productColorIds)
					.forEach((colorIds) => {
						if (colorIds) {
							productColorIds.push(...colorIds);
						}
					});

				let removedColorIds = productColorIds
					.filter((id) => !quotationColorIds.includes(id))
					.map((id) => parseInt(id));
				setRemovedColorIds(removedColorIds);
			} else {
				let productWiseColorIds = {};
				orderStyles.map((styleItem) => {
					productWiseColorIds[styleItem.productId] = styleItem.colorWiseSizeQuantityPairList?.map(
						(colorId) => colorId.id
					);
				});
				setProductWiseColorIds(productWiseColorIds);
			}
		}
	}, [orderStyles]);

	useEffect(() => {
		setGroupData(getGroupPoData(filterStylesData()));
	}, [orderStyles, hideStatusType]);

	const onPlaceOrder = async () => {
		setErrorDesign({});

		const missingProgramTypes = {};
		const missingSalesMargins = {};
		const missingDeals = {};
		orderStyles.forEach((item) => {
			if (!item.isBaseRfq) {
				return;
			}
			if (!item.programTypeId && !item.programType?.id) {
				missingProgramTypes[item.id] = 'Program type is mandatory';
			}
			if (
				(item.salesMarginPercentage === null ||
					item.salesMarginPercentage === undefined ||
					item.salesMarginPercentage === '') &&
				(item.salesMarginFobAmount === null ||
					item.salesMarginFobAmount === undefined ||
					item.salesMarginFobAmount === '')
			) {
				missingSalesMargins[item.id] = 'Sales margin is mandatory';
			}
			if (!item.projectDeal?.id && !item.dealId) {
				missingDeals[item.id] = 'Deal is mandatory';
			}
		});
		if (Object.keys(missingProgramTypes).length > 0) {
			setProgramTypeErrors(missingProgramTypes);
			return;
		}
		setProgramTypeErrors({});
		if (Object.keys(missingSalesMargins).length > 0) {
			setSalesMarginErrors(missingSalesMargins);
			return;
		}
		setSalesMarginErrors({});
		if (Object.keys(missingDeals).length > 0) {
			setDealErrors(missingDeals);
			return;
		}
		setDealErrors({});

		const payload = orderStyles.map((order) => {
			const hasFobAmount =
				order.salesMarginFobAmount !== null &&
				order.salesMarginFobAmount !== undefined &&
				order.salesMarginFobAmount !== '';
			const hasPercentage =
				order.salesMarginPercentage !== null &&
				order.salesMarginPercentage !== undefined &&
				order.salesMarginPercentage !== '';
			const salesMarginFields = hasFobAmount
				? { salesMarginFobAmount: order.salesMarginFobAmount, salesMarginPercentage: undefined }
				: hasPercentage
					? { salesMarginPercentage: order.salesMarginPercentage, salesMarginFobAmount: undefined }
					: {};

			const orderWithoutDeal = { ...order };
			delete orderWithoutDeal.dealName;
			delete orderWithoutDeal.projectDeal;

			return {
				...orderWithoutDeal,
				...salesMarginFields,
				dealId: order.projectDeal?.id || order.dealId
			};
		});

		const splitDealIds = await askDealSplit(orderId, { productsToAdd: payload });
		if (splitDealIds === null) return;

		setIsPlacingOrder(true);
		dispatch(openLoader());
		await ApiClient.POST(
			'addStyleToOrder',
			payload,
			`${orderId}/add-style${dealSplitQuery(splitDealIds)}`
		)
			.then(({ data }) => {
				dispatch(closeLoader());
				setIsPlacingOrder(false);
				getProjectDetails(orderId);
				if (data.success) {
					toastSuccess(data.message);
					onAddSuccess();
				} else {
					toastError(data.message);
				}
			})
			.catch(({ response }) => {
				setIsPlacingOrder(false);
				dispatch(closeLoader());
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	// Styles that came from an AI-assisted PO extraction are submitted through their own API.
	const onPlaceAiOrder = async () => {
		const aiStyles = orderStyles.filter((style) => style.isFromPoExtraction);

		const { missingProgramTypes, missingSalesMargins } = validateOrderStyles(aiStyles);
		const missingDeals = {};
		aiStyles.forEach((style) => {
			if (!style.projectDeal?.id && !style.dealId) {
				missingDeals[style.id] = 'Deal is mandatory';
			}
		});
		setProgramTypeErrors(missingProgramTypes);
		setSalesMarginErrors(missingSalesMargins);
		setDealErrors(missingDeals);
		if (
			[missingProgramTypes, missingSalesMargins, missingDeals].some(
				(item) => Object.keys(item).length > 0
			)
		) {
			return;
		}

		if (aiStyles.some((style) => !isFinite(Number(style.productId)))) {
			toastError('Please select a product for every style');
			return;
		}

		const getSizePrice = (style, color, size) => {
			if (style.buyerQuotationType === 'SIZEWISE')
				return style.sizeWiseBuyerPrice?.[size.id] ?? size.price;
			if (style.buyerQuotationType === 'COLORWISE')
				return style.colorWiseBuyerPrice?.[color.id] ?? size.price;
			if (style.buyerQuotationType === 'DESIGNWISE') return style.designWiseBuyerPrice ?? size.price;
			return size.price;
		};

		const requestBody = {
			extractionResultId: aiStyles[0]?.extractionResultId || null,
			poDocIdList: [...new Set(aiStyles.flatMap((style) => style.poDocIdList || []))],
			dealId: aiStyles[0]?.projectDeal?.id || aiStyles[0]?.dealId,
			designs: aiStyles.map((style) => ({
				designIdentifier: style.designIdentifier,
				productId: style.productId,
				referenceNumber: style.referenceNumber || style.productReferenceNumber,
				poNumber: style.poNumber,
				poReceiveDate: style.poReceiveDate,
				deliveryDate: style.deliveryDate,
				dealId: style.projectDeal?.id || style.dealId,
				programTypeId: style.programType?.id || style.programTypeId,
				salesMarginPercentage: style.salesMarginPercentage,
				salesMarginFobAmount: style.salesMarginFobAmount,
				priceType: style.priceType,
				unitOfMeasurementQty: style.unitOfMeasurementQty,
				colors: (style.colorWiseSizeQuantityPairList || []).map((color) => ({
					colorId: color.id,
					colorName: color.colorName || color.name,
					sizes: (color.sizeQuantityPairList || []).map((size) => ({
						productSizeId: size.id,
						sizeLabel: size.sizeLabel || size.label,
						sku: size.sku,
						quantity: size.quantity || 0,
						price: Number(getSizePrice(style, color, size) ?? 0)
					}))
				}))
			}))
		};

		const splitDealIds = await askDealSplit(orderId, {
			productsToAdd: requestBody.designs.map((design) => ({
				dealId: design.dealId || requestBody.dealId,
				productId: design.productId
			}))
		});
		if (splitDealIds === null) return;
		requestBody.splitDealIds = splitDealIds;

		dispatch(openLoader());
		try {
			const response = await addAiPoExtraction({ orderId, requestBody }).unwrap();
			if (response?.success === false) {
				toastError(response?.message || 'Something went wrong! Please try again.');
				return;
			}
			toastSuccess(response?.message || 'Style added successfully');
			getProjectDetails(orderId);
			onAddSuccess();
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong! Please try again.');
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.product.SetPackStyleFlag', setSetPackStyleFlag);
				await fetchEnumValues('info.nitex.app.materials.Season', setSeasons);
			} catch {}
		})();
	}, []);

	const getProgramTypePayload = (response) => {
		if (Array.isArray(response)) return response;
		if (Array.isArray(response?.data)) return response.data;
		return [];
	};

	const fetchProgramTypes = async () => {
		try {
			const response = await fetchProgramTypeTrigger().unwrap();
			setProgramTypeOptionList(getProgramTypePayload(response));
		} catch (error) {
			toastError(error?.data?.message || 'Failed to fetch program types.');
		}
	};

	useEffect(() => {
		fetchProgramTypes();
	}, []);

	const renderProgramTypeOptions = () => {
		return programTypeOptions.map((programType) => ({
			...programType,
			value: programType.name
		}));
	};

	const createNewProgramType = async (name, setIsOpen) => {
		const trimmedName = String(name || '').trim();
		if (!trimmedName) return;

		try {
			await createProgramType({ name: trimmedName }).unwrap();
			await fetchProgramTypes();
			toastSuccess('Program type created successfully');
			if (typeof setIsOpen === 'function') {
				setIsOpen(false);
			}
		} catch (error) {
			toastError(error?.data?.message || 'Failed to create program type.');
		}
	};

	const onProgramTypeSelect = (singlePo, programType) => {
		const updatedArray = orderStyles.map((item) => {
			if (item.id === singlePo.id) {
				return {
					...item,
					programTypeId: programType?.id || null,
					programType: programType
						? {
								id: programType.id,
								name: programType.name
							}
						: null
				};
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
	};

	const formatProjectDealOptions = (dealList) => {
		if (!Array.isArray(dealList)) {
			return [];
		}
		return dealList.map((deal) => ({
			...deal,
			value: deal?.dealName
		}));
	};

	const handleDealChange = (singlePoId, selectedDeal) => {
		const updatedArray = orderStyles.map((item) => {
			if (item.id === singlePoId) {
				return {
					...item,
					projectDeal: selectedDeal
						? {
								id: selectedDeal.dealId,
								dealName: selectedDeal.dealName
							}
						: null
				};
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
		if (selectedDeal?.dealId) {
			setDealErrors((prev) => {
				const updated = { ...prev };
				delete updated[singlePoId];
				return updated;
			});
		}
	};

	const onChangePO = (array, id, keyPath, value) => {
		const updatedArray = array.map((item) => {
			if (item.id === id) {
				const updatedItem = {
					...item,
					rfqId: id,
					[keyPath]: value
				};

				return updatedItem;
			}
			return item;
		});
		setOrderStyles(updatedArray);
		setGroupData(getGroupPoData(updatedArray));
		return updatedArray;
	};

	const productOptions = (
		collectionData?.data?.length
			? collectionData.data
			: (folderCollectionData?.data || []).map((item) => item?.productResponse || item)
	)
		.filter(
			(item) =>
				(item.fabricName && item.fabricDetails) ||
				item?.isSet ||
				item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
		)
		.map((item) => ({ ...item, value: item.referenceNumber }));
	const onProductChange = (styleId, product) => {
		const updatedStyles = orderStyles.map((style) =>
			style.id === styleId
				? {
						...style,
						// the parent list still holds the product id the extraction came with
						extractionProductId: style.extractionProductId ?? style.productId,
						productId: product.id,
						referenceNumber: product.referenceNumber,
						productReferenceNumber: product.referenceNumber,
						docUrl: product.designDocuments?.[0]?.docUrl || '',
						documentResponseList: product.designDocuments || []
					}
				: style
		);
		setOrderStyles(updatedStyles);
		setGroupData(getGroupPoData(updatedStyles));
	};

	// An extracted style shows the image of the product picked in its dropdown.
	const getStyleImage = (singlePo) => {
		const selectedProduct = singlePo.isFromPoExtraction
			? productOptions.find((product) => product.referenceNumber === singlePo.referenceNumber)
			: null;
		return (
			selectedProduct?.designDocuments?.[0]?.docUrl ||
			singlePo?.documentResponseList[0]?.docUrl ||
			singlePo?.docUrl
		);
	};

	// Several extracted styles can share a product, so the position in the list is sent along with it.
	const removePoExtractionStyle = (singlePo) => {
		const index = orderStyles.findIndex((style) => style.id === singlePo.id);
		onRemovePoExtractionStyle(singlePo.extractionProductId ?? singlePo.productId, index);
	};

	const renderProductDropdown = (singlePo) => (
		<CustomDropdown
			title="Select Item"
			items={productOptions}
			selectedItem={singlePo.referenceNumber || ''}
			onItemClick={(product) => onProductChange(singlePo.id, product)}
			toolTipLength={12}
			customDesign={(item) => (
				<div className="d-flex align-items-center gap-2">
					{item.designDocuments?.[0]?.docUrl && (
						<img src={item.designDocuments[0].docUrl} alt={item.value} />
					)}
					<span>{item.value}</span>
				</div>
			)}
		/>
	);

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};
	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					item?.isSet || item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: item?.productId,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST',
				designIdList: designIdList
			})
		);
	};
	const filterStylesData = () => {
		if (hideStatusType) {
			return orderStyles.filter((item) => {
				switch (hideStatusType) {
					case 'piCreated':
						return item.isPiCreated === true;
					case 'piPending':
						return item.isPiCreated === false;
					case 'scCreated':
						return item.isScCreated === true;
					case 'scPending':
						return item.isScCreated === false;
					case 'tnaCreated':
						return item.isTNACreated === true;
					case 'tnaPending':
						return item.isTNACreated === false;
					default:
						return true;
				}
			});
		} else {
			return orderStyles;
		}
	};
	const isDisabledSubmitButton = (array) => {
		//draftStatus: "COMPLETED" for all array then it will return false other wise true
		return array.some((item) => item.draftStatus !== 'COMPLETED');
	};

	if (!groupData) {
		// Show a loading spinner or placeholder while data is being initialized
		return <div>Loading...</div>;
	}

	return (
		<>
			{dealSplitModal}
			<>
				<h3 className="semibold-14 pt-2">New style added</h3>
				<div className="accordion" id="accordionExample">
					{groupData?.map((item, index) => (
						<div
							className={`card ${isOverflow ? 'overflow-visible' : ''} ${index === groupData.length - 1 ? 'last-card' : ''} ${!isDisabledSubmitButton(item?.poData) ? 'active' : ''}`}
							key={`new_style_card_${index}`}
						>
							<div className="card-header">
								<h2 className="mb-0">
									<button
										className="btn btn-link btn-block d-flex justify-content-between align-items-center text-left gray_dark_01 regular-14 weight-600 p-0"
										type="button"
										onClick={() => handleAccordionClick(item?.productReferenceNumber)}
									>
										<div className="d-flex align-items-center">
											<div className="order-header-img mr-2">
												<CommonImage isStatic={false} src={item?.imgUrl} />
											</div>
											<p className="medium-14">
												{item.productReferenceNumber}{' '}
												<span className="regular-12 gray_dark_02">
													({item?.name})
												</span>
												{!isDisabledSubmitButton(item?.poData) && (
													<CommonImage
														src="/icons/check-bg-green.svg"
														className="ml-2"
														alt=""
														width="18"
													/>
												)}
											</p>
										</div>

										<div className="arrow-down">
											<CommonImage
												src="/icons/down-arrow-gray-dark-02.svg"
												alt=""
												width="18"
											/>
										</div>
									</button>
								</h2>
							</div>

							<div className={`collapse show`}>
								{item?.poData.map((singlePo) => (
									<div
										key={singlePo.id}
										className={`card-body border-0 designs-info-section mt-0 radius-6 ${singlePo.draftStatus === 'COMPLETED' && isDisabledSubmitButton(item?.poData) ? 'active' : ''}`}
									>
										<div className="designs-row order-desing-row">
											<div
												className={`single-design p-0 m-0 border-0 ${!singlePo.isBaseRfq && 'd-block'}`}
											>
												{singlePo.isBaseRfq && (
													<div className="single-design-card with-editable">
														<div className="card-top">
															<div className="edit-items">
																<div className="select-and-edit d-flex justify-content-between">
																	<div className="custom-chekbox">
																		<div className="form-group cursor-pointer">
																			<CommonImage
																				src={`/icons/delete_gray.svg`}
																				alt="delete"
																				className="delete-button add-to-favorite"
																				onClick={(e) => {
																					e.stopPropagation();
																					if (
																						singlePo.isFromPoExtraction
																					) {
																						removePoExtractionStyle(
																							singlePo
																						);
																						return;
																					}
																					onRemoveStyles(
																						singlePo.productId
																					);
																				}}
																			/>
																		</div>
																	</div>
																</div>
															</div>
															{singlePo.isFromPoExtraction && (
																<div className="missing-style position-absolute top-0 right-0">
																	{renderProductDropdown(singlePo)}
																</div>
															)}
															<>
																{singlePo?.isSet ? (
																	<div
																		onClick={() =>
																			openDesignViewModal(singlePo)
																		}
																	>
																		{renderSetImages(
																			singlePo?.documentResponseList,
																			'100%'
																		)}
																	</div>
																) : (
																	<CommonImage
																		src={getStyleImage(singlePo)}
																		alt={singlePo.name}
																		isStatic={false}
																		onClick={() =>
																			openDesignViewModal(singlePo)
																		}
																	/>
																)}
															</>
														</div>
													</div>
												)}
												<div className="title-and-form">
													{!singlePo.isBaseRfq && (
														<div className="split-order-form d-block">
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-4 ">
																		<div className="form-group mb-2">
																			<label for="design-no">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																	<div className="col-md-4">
																		<div className="form-group mb-2">
																			<label for="po-no">
																				ETD (dd/mm/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)}

													{/* {singlePo.isEdit ? ( */}
													{singlePo.isBaseRfq && (
														<div className="split-order-form pt-4">
															{/* Row 1: Design No, PO No, PO received date, ETD */}
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="design-no">
																				Design No
																			</label>
																			<input
																				type="text"
																				placeholder="Design No"
																				name="productReferenceNumber"
																				id={`productReferenceNumber_${singlePo.id}`}
																				value={
																					singlePo.productReferenceNumber
																				}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'productReferenceNumber',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="po-no">
																				PO No
																			</label>
																			<input
																				type="text"
																				id=""
																				placeholder="PO No"
																				value={singlePo.poNumber}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poNumber',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="po-receive-date">
																				PO received date
																			</label>
																			<input
																				type="date"
																				placeholder="02/05/2024"
																				id=""
																				value={singlePo.poReceiveDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'poReceiveDate',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label htmlFor="etd">
																				ETD (dd/mm/yyyy)
																			</label>
																			<input
																				type="date"
																				id=""
																				placeholder="02/05/2024"
																				value={singlePo.deliveryDate}
																				onChange={(e) =>
																					onChangePO(
																						orderStyles,
																						singlePo.id,
																						'deliveryDate',
																						e.target.value
																					)
																				}
																			/>
																		</div>
																	</div>
																</div>
															</div>
															{/* Row 2: Sales Margin, Program Type, UOM, Deal */}
															<div className="single-design-input">
																<div className="row">
																	<div className="col-md-3">
																		<div className="form-group mb-2">
																			<label>
																				Sales Margin (on FOB)*
																			</label>
																			<div className="d-flex sales-margin-inputs">
																				<div className="sales-margin-input-wrapper">
																					<span className="sales-margin-prefix">
																						%
																					</span>
																					<input
																						type="number"
																						id={`salesMarginPercentage_${singlePo.id}`}
																						placeholder="Percentage"
																						value={
																							singlePo.salesMarginPercentage ??
																							''
																						}
																						onKeyPress={
																							validateFloatNumber
																						}
																						className="percentage-value"
																						onChange={(e) =>
																							onChangePO(
																								orderStyles,
																								singlePo.id,
																								'salesMarginPercentage',
																								e.target
																									.value ===
																									''
																									? ''
																									: parseFloat(
																											e
																												.target
																												.value
																										)
																							)
																						}
																						disabled={
																							singlePo.salesMarginFobAmount !==
																								null &&
																							singlePo.salesMarginFobAmount !==
																								undefined &&
																							singlePo.salesMarginFobAmount !==
																								''
																						}
																					/>
																				</div>
																				<span className="gray_dark_02 regular-14 pt-2">
																					{' '}
																					or{' '}
																				</span>
																				<div className="sales-margin-input-wrapper">
																					<span className="sales-margin-prefix">
																						$
																					</span>
																					<input
																						type="number"
																						id={`salesMarginFobAmount_${singlePo.id}`}
																						placeholder="Amount"
																						value={
																							singlePo.salesMarginFobAmount ??
																							''
																						}
																						onKeyPress={
																							validateFloatNumber
																						}
																						onChange={(e) =>
																							onChangePO(
																								orderStyles,
																								singlePo.id,
																								'salesMarginFobAmount',
																								e.target
																									.value ===
																									''
																									? ''
																									: parseFloat(
																											e
																												.target
																												.value
																										)
																							)
																						}
																						disabled={
																							singlePo.salesMarginPercentage !==
																								null &&
																							singlePo.salesMarginPercentage !==
																								undefined &&
																							singlePo.salesMarginPercentage !==
																								''
																						}
																					/>
																				</div>
																			</div>
																			{salesMarginErrors?.[
																				singlePo.id
																			] && (
																				<p className="error">
																					{
																						salesMarginErrors[
																							singlePo.id
																						]
																					}
																				</p>
																			)}
																		</div>
																	</div>
																	<div className="col-md-3">
																		<div className="form-group composition-item mb-0">
																			<label>Program Type*</label>
																			<div className="d-flex composition-and-qty">
																				<div className="w-100">
																					<CustomDropdown
																						title="Select program type"
																						isShowLabel={true}
																						isShowSearch={true}
																						items={renderProgramTypeOptions()}
																						onAddNewButtonClick={
																							createNewProgramType
																						}
																						showAddNewButton={isAdmin()}
																						selectedItem={
																							(
																								singlePo?.programType ||
																								programTypeOptions.find(
																									(item) =>
																										item.id ===
																										singlePo?.programTypeId
																								) ||
																								{}
																							).name || ''
																						}
																						onItemClick={(
																							value
																						) =>
																							onProgramTypeSelect(
																								singlePo,
																								value
																							)
																						}
																					/>
																					{programTypeErrors?.[
																						singlePo.id
																					] && (
																						<p className="error regular-12 mt-1">
																							{
																								programTypeErrors[
																									singlePo
																										.id
																								]
																							}
																						</p>
																					)}
																				</div>
																			</div>
																		</div>
																	</div>
																	{(singlePo?.isPack ||
																		singlePo?.isSet) && (
																		<div className="col-md-3">
																			<div className="form-group composition-item mb-0">
																				<label>UOM</label>
																				<div className="d-flex composition-and-qty">
																					<input
																						type="text"
																						className="input-percentage"
																						placeholder={
																							singlePo?.isPack
																								? 'Pack'
																								: singlePo?.isSet
																									? 'Set'
																									: ''
																						}
																						onChange={(e) =>
																							onChangePO(
																								orderStyles,
																								singlePo.id,
																								'unitOfMeasurementType',
																								e.target.value
																							)
																						}
																						value={
																							singlePo?.isPack
																								? 'Pack'
																								: singlePo?.isSet
																									? 'Set'
																									: ''
																						}
																						disabled={true}
																					/>

																					<div className="select-component">
																						<div className="form-group size36 mb-2">
																							<div className="select-component">
																								<input
																									type="number"
																									min="0.1"
																									max="100"
																									className="w-100"
																									placeholder="Write qty"
																									onChange={(
																										e
																									) =>
																										onChangePO(
																											orderStyles,
																											singlePo.id,
																											'unitOfMeasurementQty',
																											e
																												.target
																												.value
																										)
																									}
																									value={
																										singlePo.unitOfMeasurementQty
																									}
																								/>
																							</div>
																						</div>
																					</div>
																				</div>
																			</div>
																		</div>
																	)}
																	<div className="col-md-3">
																		<div className="form-group composition-item mb-0">
																			<label>Deal*</label>
																			<div className="d-flex composition-and-qty">
																				<div className="w-100">
																					<CustomDropdown
																						title="Select"
																						selectedItem={
																							(
																								singlePo?.projectDeal ||
																								projectDealList?.find(
																									(item) =>
																										item.dealId ===
																										singlePo?.dealId
																								) ||
																								{}
																							).dealName || ''
																						}
																						items={formatProjectDealOptions(
																							projectDealList
																						)}
																						toolTipLength={25}
																						onItemClick={(
																							value
																						) =>
																							handleDealChange(
																								singlePo.id,
																								value
																							)
																						}
																					/>
																					{dealErrors?.[
																						singlePo.id
																					] && (
																						<p className="error regular-12 mt-1">
																							{
																								dealErrors[
																									singlePo
																										.id
																								]
																							}
																						</p>
																					)}
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													)}
												</div>
											</div>
											<ColorSizeBreakDownTable
												item={{ ...singlePo, isEdit: true }}
												stylesData={orderStyles}
												setOrderStyles={setOrderStyles}
												quoteId={singlePo.id}
												isDrafted={true}
												error=""
												errorDesign={errorDesign}
												setIsOverflow={setIsOverflow}
												poItemIdentifier={poItemIdentifier}
												isDisabled={false}
												pOSegmentationType={pOSegmentationType}
												isFromPoAutomation={!!singlePo.isFromPoExtraction}
												dropDownselectedProductId={
													singlePo.isFromPoExtraction ? singlePo.productId : null
												}
											/>
										</div>
									</div>
								))}
							</div>
						</div>
					))}
					<div className="footer-button text-right mb-2">
						<button
							className="button size32"
							onClick={hasPoExtractionStyle ? onPlaceAiOrder : onPlaceOrder}
						>
							Submit
						</button>
					</div>
				</div>
			</>
		</>
	);
};

export default AddStyleFromCurrentOrder;
